import * as _ from 'lodash'
import { PaymentBO } from "./PaymentBO";
import { ErrorMessage_restate } from "../PA.Core/Basic/MessageUtils";

export class PaymentBOValidations {

    static async Validate(aPmt: PaymentBO): Promise<void> {
        aPmt.ValidateMessageProvider.clearMessages()

        let bWasActive = aPmt.WB_Payment.previous("DELETED_IND") !== "1"
        let bDeletedViaSave = bWasActive && !aPmt.WB_Payment.isActive

        if (bDeletedViaSave) {
            let bIsPostApproval = aPmt.PrimaryWorkflowProcessor.CurrentState.StatusTypeIsPostApproval

            ErrorMessage_restate(
                aPmt.ValidateMessageProvider,
                bIsPostApproval,
                "CantDeletePostApproval",
                "This payment cannot be deleted after it has been approved."
            )

            if (bIsPostApproval) {
                return
            }
        }

        if (!aPmt.WB_Payment.isActive)
            return

        let arrClosedPlanNames: string[] = []
        let arrClosedOrCancelledTacticNames: string[] = []

        _.forEach(aPmt.WB_Payment.WB_PaymentEventSpendRows, (spend) => {
            if (!spend.isActive) return

            let tactic = spend.WB_TPM_EventSpendRow
            let evt = tactic.WB_TPM_EventRow
            let pln = evt.WB_PlanRow

            if (pln && pln.WB_WorkflowStatusRow && pln.WB_WorkflowStatusRow.StatusTypeClosed) {
                arrClosedPlanNames.push(pln.cif.PLAN_NAME)
            }

            let evtStatus = evt.WB_WorkflowStatusRow
            let tacticStatus = tactic.WB_WorkflowStatusRow
            let bClosedOrCancelled = (evtStatus && (evtStatus.StatusTypeClosed || evtStatus.StatusTypeCancelled))
                || (tacticStatus && (tacticStatus.StatusTypeClosed || tacticStatus.StatusTypeCancelled))
            if (bClosedOrCancelled) {
                arrClosedOrCancelledTacticNames.push(tactic.cif.EVENT_SPEND_NAME)
            }
        })

        arrClosedPlanNames = _.uniq(_.filter(arrClosedPlanNames, p => !_.isEmpty(p)))
        arrClosedOrCancelledTacticNames = _.uniq(_.filter(arrClosedOrCancelledTacticNames, p => !_.isEmpty(p)))

        if (!_.isEmpty(arrClosedPlanNames)) {
            let message = arrClosedPlanNames.length == 1
                ? "a closed plan: " + _.first(arrClosedPlanNames)
                : "closed plans: " + _.join(arrClosedPlanNames, ", ")
            ErrorMessage_restate(aPmt.ValidateMessageProvider, true, "ClosedPlan", "This payment cannot be saved. It contains tactics from " + message + ". Please remove these tactics and create a new payment if needed.")
        }

        if (!_.isEmpty(arrClosedOrCancelledTacticNames)) {
            let message = arrClosedOrCancelledTacticNames.length == 1
                ? "a closed or cancelled tactic: " + _.first(arrClosedOrCancelledTacticNames)
                : "closed or cancelled tactics: " + _.join(arrClosedOrCancelledTacticNames, ", ")
            ErrorMessage_restate(aPmt.ValidateMessageProvider, true, "ClosedOrCancelledTactic", "This payment cannot be saved. It contains " + message + ". Please remove these tactics and create a new payment if needed.")
        }

        let arrActiveClaimRows = _.filter(aPmt.WB_Payment.WB_PaymentClaimRows, (aRow) => aRow.isActive)

        if (aPmt.bSingleClaimMode) {
            ErrorMessage_restate(
                aPmt.ValidateMessageProvider,
                arrActiveClaimRows.length > 1,
                "SingleClaimPolicyViolation",
                "This payment type allows only a single claim, but " + arrActiveClaimRows.length + " are attached. Please remove the extra claims."
            )
        }

        // Full allocation coverage is an Approved-transition gate, not a DRAFT-time requirement -- a
        // Check Request still being assembled in DRAFT shouldn't be forced to already cover the full
        // Claim Amount; that's only enforced once the user is moving it to/resaving it as APPROVED.
        let bMovingToApproved = aPmt.PrimaryWorkflowProcessor?.NextState?.StatusTypeApproved

        if (bMovingToApproved && !_.isEmpty(arrActiveClaimRows)) {
            let totalClaimAllocation = _.sumBy(arrActiveClaimRows, (aRow) => aRow.cif.PAYMENT_AMT)
            let bAllocationMismatch = Math.abs(totalClaimAllocation - aPmt.WB_Payment.cif.PAYMENT_AMT) > 0.01

            ErrorMessage_restate(
                aPmt.ValidateMessageProvider,
                bAllocationMismatch,
                "ClaimAllocationMismatch",
                "This payment cannot be saved. Claim allocations total " + totalClaimAllocation.toFixed(2) +
                " but the payment amount is " + aPmt.WB_Payment.cif.PAYMENT_AMT.toFixed(2) + "."
            )
        }
    }

    static async ValidateBeforeDelete(aPmt: PaymentBO): Promise<void> {
        // A brand-new, never-saved Payment has no workflow history to protect -- let it be
        // discarded unconditionally instead of running the post-approval status check below.
        if (aPmt?.WB_Payment?.isActiveNew) {
            return
        }

        let currentState = aPmt.PrimaryWorkflowProcessor.CurrentState
        let bCanDelete = currentState.StatusTypeInitial || currentState.StatusTypePendingApproval

        ErrorMessage_restate(
            aPmt.DeleteMessageProvider,
            !bCanDelete,
            'CantDeletePostApproval',
            'Unable to delete a Payment that is not in Initial or Pending Approval status.'
        )
    }

}
